import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Hooks and services
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';
import { uploadApkFile } from '../../../../services/apk.service';

// Utils
import { parseError } from '../../../../utils';

// Common Components
import Paper from '@mui/material/Paper';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import CustomModal from '../../../common/withModal';

export const UploadApkModal = ({ setModalOpen, forceReloadTable }: { setModalOpen: (open: boolean) => void; forceReloadTable: () => void }) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { notifyMessage } = useNotifyMessage();

  // Explicitly type the form values to handle apkFile as a File object
  interface FormValues {
    file: File | null;
    version: string;
    isTestBuild: boolean;
    isCurrentMarketplaceBuild: boolean;
  }

  const handleFileSet = async (file: File | null) => {
    await formik.setFieldValue('file', file);

    if (file) {
      // Extract version from the filename (e.g. 'somefile-1.0.apk', 'somefile-2.34.4.5.apk')
      const versionMatch = file.name.match(/(\d+(\.\d+)+)/); // Supports versions like 1.0, 2.34.4.5
      if (versionMatch) {
        await formik.setFieldValue('version', versionMatch[0]);
        await formik.setFieldTouched('version', true);
      }

      // Check if the filename includes the word 'test'
      const isTestBuild = file.name.toLowerCase().includes('test');
      await formik.setFieldValue('isTestBuild', isTestBuild);
      await formik.setFieldTouched('isTestBuild', true);
    }

    // Validate the form after making changes
    await formik.validateForm();
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      file: null,
      version: '',
      isTestBuild: false,
      isCurrentMarketplaceBuild: false,
    },
    validationSchema: Yup.object({
      version: Yup.string()
        .required('Version is required')
        .matches(/^(\d+(\.\d+)+)$/, 'Version must be in x.y.z format'),
      isTestBuild: Yup.boolean(),
      isCurrentMarketplaceBuild: Yup.boolean(),
      file: Yup.mixed<File>()
        .nullable()
        .required('APK file is required')
        .test('fileType', 'Only .apk files are allowed', (value) => value && value.type === 'application/vnd.android.package-archive'),
    }),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: async () => {},
  });

  const handleSubmitForm = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const { version, isTestBuild, isCurrentMarketplaceBuild, file } = formik.values;

    try {
      setIsSubmitting(true);

      if (!file) throw new Error('No file selected');

      // Upload
      await uploadApkFile({ file, version, isTestBuild, isCurrentMarketplaceBuild });

      // Notify success
      notifyMessage('Release uploaded successfully', 'success');

      formik.resetForm(); // reset form
      setModalOpen(false); // close modal
      forceReloadTable(); // reload table
    } catch (err: any) {
      console.error(err);
      notifyMessage(parseError(err), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal
      title='Upload new APK Release'
      isOpen={true}
      isValid={formik.isValid}
      isLoading={isSubmitting}
      setIsOpen={setModalOpen}
      maxWidth='md'
      onConfirm={handleSubmitForm}
    >
      <Paper elevation={3}>
        <form onSubmit={(e) => handleSubmitForm(e)} style={{ width: 'auto', height: 'auto', padding: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {/* File input for APK file */}
            <input
              id='file'
              type='file'
              name='file'
              accept='.apk'
              onChange={(e) => {
                handleFileSet(e.currentTarget.files ? e.currentTarget.files[0] : null);
              }}
              style={{ padding: '8px', fontSize: '14px' }}
            />
            {formik.errors.file && formik.touched.file && <div style={{ color: 'red' }}>{formik.errors.file}</div>}

            <TextField
              id='version'
              name='version'
              label='Version number'
              value={formik.values.version ?? ''}
              onChange={formik.handleChange}
              placeholder='Version (e.g., 1.0.0)'
              variant='standard'
              size='small'
              fullWidth
              required
              margin='dense'
            />

            <FormControlLabel
              control={<Checkbox id='isTestBuild' name='isTestBuild' checked={formik.values.isTestBuild ?? false} onChange={formik.handleChange} />}
              label='Test Build'
            />

            <FormControlLabel
              control={
                <Checkbox
                  id='isCurrentMarketplaceBuild'
                  name='isCurrentMarketplaceBuild'
                  checked={formik.values.isCurrentMarketplaceBuild ?? false}
                  onChange={formik.handleChange}
                />
              }
              label='Current Marketplace Build'
            />
          </div>
        </form>
      </Paper>
    </CustomModal>
  );
};
